import { Component } from "@kluntje/core";
import { toggleClass, hasClass, removeClass, getParent } from "@kluntje/js-utils/lib/dom-helpers";
import { VISIBLE } from "Helper/cssClasses";

class FRAFlyout extends Component {
  private get isInsideClickAllowed(): boolean {
    return this.hasAttribute("allow-inside-clicks");
  }

  private clickHandlers = new Set<(e: Event) => void>();

  constructor() {
    super({
      ui: {
        container: ".fra-m-flyout__container :-one",
        items: ".fra-m-flyout__link"
      },

      events: [
        {
          event: "fra-page-header-action-clicked",
          target: "window",
          handler: "handleHeaderActionClick",
        },
      ],
    });
  }

  /**
   * Handles events triggered by language action clicks.
   *
   * @param {CustomEvent<{ action: string }>} e - click event
   */
  handleHeaderActionClick(e: CustomEvent<{ action: string }>): void {
    if (e.detail.action !== "language") {
      return;
    }

    this.open();
  }

  /**
   * Opens the flyout (animated). A closing callback is created and stored.
   * If the flyout is already visible it is closed.
   */
  open(): void {
    const isVisible = this.isVisible();
    if (isVisible) {
      this.close();
      return;
    }

    this.ui.items.forEach((item: HTMLElement) => {
      item.setAttribute("tabindex", "0");
    })

    this.ui.container.style.maxHeight = !isVisible ? `${this.computeContainerHeight()}px` : "";
    toggleClass(this, VISIBLE, !isVisible);

    // next click closes the flyout
    if (!isVisible) {
      const handler = this.createWindowCallback();
      this.clickHandlers.add(handler);
      window.addEventListener("click", handler);
    }
  }

  /**
   * Closes the flyout and removed all active click listeners.
   */
  close(): void {
    if (!this.isVisible()) {
      return;
    }

    this.ui.items.forEach((item: HTMLElement) => {
      item.setAttribute("tabindex", "-1");
    })

    removeClass(this, VISIBLE);
    this.ui.container.style.maxHeight = "";
    this.clickHandlers.forEach(handler => window.removeEventListener("click", handler));
    this.clickHandlers.clear();
  }

  /**
   * Creates a callback function that deals with window clicks.
   * @param {Event} e
   * @returns {*} the callback
   */
  private createWindowCallback(): (e: Event) => void {
    return (e: Event) => {
      if (this.isInsideClickAllowed && e.target) {
        const parentOrSelf = getParent(e.target as HTMLElement, ".fra-m-flyout");
        if (this === parentOrSelf) {
          e.preventDefault();
          return;
        }
      }

      this.close();
    };
  }

  private isVisible(): boolean {
    return hasClass(this, VISIBLE);
  }

  /**
   * Helper method to compute the container's height if not collapsed.
   *
   * @private
   * @returns {number} height of the container
   */
  private computeContainerHeight(): number {
    this.ui.container.style.visibility = "hidden";
    this.ui.container.style.zIndex = "-1";
    this.ui.container.style.transitionDuration = "1";
    this.ui.container.style.maxHeight = "none";
    const contentHeight = this.ui.container.offsetHeight;

    this.ui.container.removeAttribute("style");
    // trigger css redraw to prevent transition interferences
    /* eslint-disable-next-line @typescript-eslint/no-unused-expressions */
    this.ui.container.offsetHeight;

    return contentHeight;
  }
}

customElements.define("fra-m-flyout", FRAFlyout);

export default FRAFlyout;
